import { createApi } from "@reduxjs/toolkit/query/react";

import apiBaseQuery from "./axiosBaseQuery";

export const DASHBOARD_API_REDUCER_KEY = "dashboardApi";

export const dashboardApi = createApi({
  reducerPath: DASHBOARD_API_REDUCER_KEY,
  baseQuery: apiBaseQuery,
  tagTypes: ["dashboard"],
  endpoints: (builder) => ({
    getDashboardData: builder.query({
      query: ({ year }) => {
        if (!year) {
          return;
        }
        return {
          url: `/emissions/dashboard/?year=${year}`,
        };
      },
      providesTags: ["dashboard"],
    }),
    getDashboardScope3: builder.query({
      query: () => {
        return {
          url: `/reports/dashboardv2/`,
        };
      },
      providesTags: ["dashboard"],
    }),
    getDashboardScope4: builder.query({
      query: ({ startDate, endDate }) => {
        // Construct the final URL with query parameters
        const url = `/reports/fetchdashboardv2nodewise/?fromDate=${startDate}&toDate=${endDate}`;
        return {
          url: url,
        };
      },
      providesTags: ["dashboard"],
    }),

    getTopCategories: builder.query({
      query: ({ year }) => {
        if (!year) {
          return;
        }
        return {
          url: `/emissions/charts/emission-categories?year=${year}`,
        };
      },
      providesTags: ["dashboard"],
    }),

    getAllCategories: builder.query({
      query: ({ scope, start_date, end_date, node }) => {
        let queryParams = [`scope=${scope}`];
        if (start_date) {
          queryParams.push(`start_date=${start_date}`);
        }
        if (end_date) {
          queryParams.push(`end_date=${end_date}`);
        }
        if (node) {
          queryParams.push(`taxonomy=${node}`);
        }

        const url = `emissions/charts/emission-categories?${queryParams.join(
          "&"
        )}`;
        return {
          url: url,
        };
      },
      providesTags: ["dashboard"],
    }),
    getAllCategories1: builder.query({
      query: ({ scope, base_year, target_year, node }) => {
        // Format the base year and target year
        const start_date = `${base_year}-01`; // Base year starts in January
        const end_date = `${target_year}-12`; // Target year ends in December
        const scopeNumber =
          scope === "Scope 1" ? 1 : scope === "Scope 2" ? 2 : 3;

        // By default, scope is always sent
        let queryParams = [`scope=${scopeNumber}`];

        // Add formatted start_date and end_date
        queryParams.push(`start_date=${start_date}`);
        queryParams.push(`end_date=${end_date}`);

        // Conditionally add the node (taxonomy) if it exists
        if (node) {
          queryParams.push(`taxonomy=${node}`);
        }

        // Construct the final URL with query parameters
        const url = `emissions/charts/emission-categories?${queryParams.join(
          "&"
        )}`;
        return {
          url: url,
        };
      },
      providesTags: ["dashboard"],
    }),

    getTopSpendGroupEmission: builder.query({
      query: ({ year }) => {
        if (!year) {
          return;
        }
        return {
          url: `/emissions/charts/sector-alias?year=${year}&limit=100`,
        };
      },
      providesTags: ["dashboard"],
    }),

    getTopSuppliersData: builder.query({
      query: ({ year }) => {
        if (!year) {
          return;
        }
        return {
          url: `/emissions/charts/suppliers?year=${year}&limit=300`,
        };
      },
      providesTags: ["dashboard"],
    }),
    getTargetAndIntensity: builder.query({
      query: () => {
        return {
          url: `/emissions/charts/targets`,
        };
      },
      providesTags: ["dashboard"],
    }),

    // old dashboard endpoints

    getProductDashboardData: builder.query({
      query: ({ company, site, quarter, year }) => {
        if (!company || !site || !quarter || !year) {
          return;
        }
        return {
          url: `/emissions/charts/products?company=${company}&company_site=${site}&quarter=${quarter}&year=${year}`,
        };
      },
      providesTags: ["dashboard"],
    }),
    getEmissionsCategoriesData: builder.query({
      query: ({ company, site, quarter, year }) => {
        if (!company || !site || !year) {
          return;
        }
        return {
          url: `/emissions/charts/emission-categories?company=${company}&company_site=${site}&quarter=${
            quarter || ""
          }&year=${year}`,
        };
      },
      providesTags: ["dashboard"],
    }),
    getCategoriesActivitiesData: builder.query({
      query: ({ category, company, site, quarter, year }) => {
        if (!category || !company || !site || !year) {
          return;
        }
        return {
          url: `/emissions/charts/emission-activities?category=${category}&company=${company}&company_site=${site}&quarter=${
            quarter || ""
          }&year=${year}`,
        };
      },
      providesTags: ["dashboard"],
    }),
    getSuppliersData: builder.query({
      query: ({ company, site, quarter, year }) => {
        if (!company || !site || !year) {
          return;
        }
        return {
          url: `/emissions/charts/suppliers?company=${company}&company_site=${site}&quarter=${
            quarter || ""
          }&year=${year}&limit=300`,
        };
      },
      providesTags: ["dashboard"],
    }),
    getSuppliersActivitiesData: builder.query({
      query: ({ id, company, site, quarter, year }) => {
        console.log();
        if (!company || !site || !year) {
          return;
        }
        return {
          url: `/emissions/charts/suppliers/${id}/activities?company=${company}&company_site=${site}&quarter=${
            quarter || ""
          }&year=${year}&limit=100`,
        };
      },
      providesTags: ["dashboard"],
    }),
    getYearWiseEmission: builder.query({
      query: ({ company, site }) => {
        if (!company) {
          return;
        }
        return {
          url: `/emissions/charts/periods?company=${company}&limit=100`,
        };
      },
      providesTags: ["dashboard"],
    }),
    getSectorAliasWiseEmission: builder.query({
      query: ({ company, site, year, quarter }) => {
        if (!company || !site) {
          return;
        }
        return {
          url: `/emissions/charts/sector-alias?company=${company}&company_site=${site}&year=${
            year || ""
          }&quarter=${quarter}&limit=100`,
        };
      },
      providesTags: ["dashboard"],
    }),
    spendGroupWiseEmission: builder.query({
      query: ({ spendGroup, year, quarter, company, site }) => {
        return {
          url: `/emissions/charts/sector-alias/activity?spend_group=${spendGroup}&year=${year}&company=${company}&quarter=${quarter}&site=${site}`,
        };
      },
      providesTags: ["dashboard"],
    }),
  }),
});

export const {
  useGetDashboardDataQuery,
  useGetDashboardScope3Query,
  useGetDashboardScope4Query,
  useGetTopCategoriesQuery,
  useGetAllCategoriesQuery,
  useGetAllCategories1Query,
  useGetTopSpendGroupEmissionQuery,
  useGetTopSuppliersDataQuery,
  useGetTargetAndIntensityQuery,
  // old
  useLazyGetDashboardDataQuery,
  useGetProductDashboardDataQuery,
  useLazyGetProductDashboardDataQuery,
  useGetEmissionsCategoriesDataQuery,
  useGetCategoriesActivitiesDataQuery,
  useGetSuppliersDataQuery,
  useLazyGetSuppliersActivitiesDataQuery,
  useGetYearWiseEmissionQuery,
  useGetSectorAliasWiseEmissionQuery,
  useSpendGroupWiseEmissionQuery,
} = dashboardApi;
